import { nanoid } from "@repo/shared/utils/id";
import { mergeColumnsConfigs } from "@repo/shared/utils/merge-columns-configs";
import { toast } from "sonner";

import { boardPage$ } from "~/pages/board/board.store";
import { getRouter } from "~/router";
import { boardFilters$ } from "../board-filters/board-filters.crud";
import { getSortedBoardFilters } from "../board-filters/board-filters.helpers";
import { datasets$ } from "../datasets";
import { activeOrganization$ } from "../organizations/active-organization.store";
import { blocks$ } from "./blocks.crud";

/** Re-merge the block's columnsConfig with its dataset's columnsConfig. */
export const remergeBlockColumnsConfig = (blockId: string) => {
  const datasetId = blocks$[blockId].datasetId.get();
  if (!datasetId) return;

  const datasetColumnsConfig = datasets$[datasetId].columnsConfig.get();
  const blockColumnsConfig = blocks$[blockId].columnsConfig.get();
  const merged = mergeColumnsConfigs(datasetColumnsConfig, blockColumnsConfig);
  blocks$[blockId].columnsConfig.set(merged);
};

/** Share a dataset with the team if it is currently private. */
export const shareDatasetIfPrivate = (datasetId: string) => {
  const isPrivate = datasets$[datasetId].private.get();
  if (!isPrivate) return;

  const datasetName = datasets$[datasetId].name.get() || "Unnamed dataset";
  datasets$[datasetId].private.set(false);
  toast.success(`"${datasetName}" is now shared with your team`);
};

/** Change a block's dataset, resetting all dataset-specific configs to defaults. */
export const changeBlockDataset = (blockId: string, newDatasetId: string) => {
  blocks$[blockId].assign({
    datasetId: newDatasetId,
    displayType: "TABLE",
    columnsConfig: {},
    riftConfig: null,
    graphConfig: {
      type: "line",
      yKeys: [],
      stacked: false,
      legend: { enabled: true, position: "top" },
    },
    statConfig: null,
    tableConfig: null,
  });
  shareDatasetIfPrivate(newDatasetId);
};

/** Compute the next available y position at the bottom of a board. */
const getNextBottomY = (boardId: string): number => {
  const allBlocks = blocks$.get() ?? {};
  let maxBottom = 0;
  for (const block of Object.values(allBlocks)) {
    if (block.boardId === boardId) {
      maxBottom = Math.max(maxBottom, block.y + block.h);
    }
  }
  return maxBottom;
};

export const createBlock = (boardId: string) => {
  const organizationId = activeOrganization$.id.get() as string;

  const blockId = nanoid();

  blocks$[blockId].set({
    id: blockId,
    organizationId,
    boardId,
    datasetId: null,
    displayType: "TABLE",
    graphConfig: undefined as any,
    hideHeader: false,
    columnsConfig: {},
    riftConfig: null,
    statConfig: null,
    tableConfig: null,
    name: "",
    description: "",
    avatar: null,
    favorite: false,
    public: false,
    x: 0,
    y: getNextBottomY(boardId),
    w: 3,
    h: 3,
    createdAt: undefined as any,
    updatedAt: undefined as any,
  });
};

export const duplicateBlock = (blockId: string) => {
  const original = blocks$[blockId].get();
  if (!original) return;

  const newId = nanoid();

  blocks$[newId].set({
    id: newId,
    organizationId: original.organizationId,
    boardId: original.boardId,
    datasetId: original.datasetId,
    name: original.name ? `${original.name} (copy)` : "Untitled block (copy)",
    description: original.description,
    displayType: original.displayType,
    tableConfig: structuredClone(original.tableConfig),
    graphConfig: structuredClone(original.graphConfig),
    columnsConfig: structuredClone(original.columnsConfig),
    riftConfig: structuredClone(original.riftConfig),
    statConfig: structuredClone(original.statConfig),
    avatar: structuredClone(original.avatar),
    hideHeader: original.hideHeader,
    favorite: false,
    public: original.public,
    x: original.x,
    y: original.y + original.h,
    w: original.w,
    h: original.h,
    createdAt: undefined as any,
    updatedAt: undefined as any,
  });
};

/**
 * Removes board filter columns whose dataset is no longer represented by any
 * block on the board. Deletes filters that lose all their columns.
 */
export const cleanupOrphanedBoardFilterColumns = (boardId: string) => {
  const allBlocks = blocks$.get() ?? {};
  const activeDatasetIds = new Set<string>();
  for (const block of Object.values(allBlocks)) {
    if (block.boardId === boardId && block.datasetId) {
      activeDatasetIds.add(block.datasetId);
    }
  }

  const filters = getSortedBoardFilters(boardId);
  for (const filter of filters) {
    const kept = filter.columns.filter((c) =>
      activeDatasetIds.has(c.datasetId),
    );
    if (kept.length === 0) {
      boardFilters$[filter.id].delete();
    } else if (kept.length < filter.columns.length) {
      boardFilters$[filter.id].columns.set(kept);
    }
  }
};

export const deleteBlock = (blockId: string) => {
  const boardId = blocks$[blockId].boardId.get();
  if (!boardId) return;

  if (boardPage$.selectedBlockId.get() === blockId) {
    boardPage$.selectedBlockId.set(null);
  }

  blocks$[blockId].delete();

  cleanupOrphanedBoardFilterColumns(boardId);
};

/** Move a block from its current board to a different board and navigate there. */
export const moveBlockToBoard = (blockId: string, targetBoardId: string) => {
  const sourceBoardId = blocks$[blockId].boardId.get();
  if (!sourceBoardId || sourceBoardId === targetBoardId) return;

  if (boardPage$.selectedBlockId.get() === blockId) {
    boardPage$.selectedBlockId.set(null);
  }

  blocks$[blockId].assign({
    boardId: targetBoardId,
    x: 0,
    y: getNextBottomY(targetBoardId),
  });

  cleanupOrphanedBoardFilterColumns(sourceBoardId);

  const router = getRouter();
  router.navigate({ to: `/boards/${targetBoardId}` });
};

/** Create a new block linked to a dataset on the target board and navigate there. */
export const sendDatasetToBoard = (
  datasetId: string,
  targetBoardId: string,
) => {
  const organizationId = activeOrganization$.id.get() as string;
  const blockId = nanoid();
  const datasetName = datasets$[datasetId].name.get();

  blocks$[blockId].set({
    id: blockId,
    organizationId,
    boardId: targetBoardId,
    datasetId,
    displayType: "TABLE",
    graphConfig: undefined as any,
    hideHeader: false,
    columnsConfig: {},
    riftConfig: null,
    statConfig: null,
    tableConfig: null,
    name: datasetName || "",
    description: "",
    avatar: null,
    favorite: false,
    public: false,
    x: 0,
    y: getNextBottomY(targetBoardId),
    w: 3,
    h: 3,
    createdAt: undefined as any,
    updatedAt: undefined as any,
  });
  shareDatasetIfPrivate(datasetId);

  const router = getRouter();
  router.navigate({ to: `/boards/${targetBoardId}` });
};
